import { useState } from "react";
import { Affiliate } from "@/entities/Affiliate";
import { motion } from "framer-motion";
import { Briefcase, CheckCircle, Package, Truck, Headphones, TrendingUp, MessageCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/components/LanguageContext";
import { useRegion } from "@/components/RegionContext";

export default function AffiliatePage() {
  const { t } = useLanguage();
  const { warehouse } = useRegion();
  const [formData, setFormData] = useState({
    full_name: "", email: "", phone: "", company_name: "",
    website: "", audience_size: "", platform: "Other", why_join: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await Affiliate.create({
      ...formData,
      status: "pending",
      affiliate_code: `B2B${Date.now().toString(36).toUpperCase()}`
    });
    setIsSuccess(true);
    setIsSubmitting(false);
    setTimeout(() => {
      setFormData({ full_name: "", email: "", phone: "", company_name: "", website: "", audience_size: "", platform: "Other", why_join: "" });
      setIsSuccess(false);
    }, 5000);
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-br from-red-600 to-gray-900 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
              <Briefcase className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-black mb-4">{t("b2b_hero")}</h1>
            <p className="text-xl md:text-2xl mb-8">{t("b2b_sub")}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 text-lg"><CheckCircle className="w-5 h-5 text-green-400" /> Wholesale Pricing</div>
              <div className="flex items-center gap-2 text-lg"><CheckCircle className="w-5 h-5 text-green-400" /> Bulk Volume Discounts</div>
              <div className="flex items-center gap-2 text-lg"><CheckCircle className="w-5 h-5 text-green-400" /> Local JDL Logistics</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why B2B */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-4xl md:text-5xl font-black text-center mb-16">{t("b2b_benefits_title")}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Package, title: "Wholesale Pricing", desc: "Tiered volume discounts for bulk tyre-changer orders." },
              { icon: Truck, title: "Local JDL Shipping", desc: `Dispatched from our ${warehouse.id.toUpperCase()} JDL warehouse — fast regional delivery.` },
              { icon: Headphones, title: "Dedicated Account Manager", desc: "Single point of contact for ordering, warranty & technical support." },
              { icon: TrendingUp, title: "Growth Partnership", desc: "Co-marketing support & exclusive territory opportunities." }
            ].map((item, i) => (
              <Card key={i} className="shadow-xl">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <item.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-black mb-4">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* WhatsApp Quick Chat */}
        <div className="max-w-7xl mx-auto px-4 mt-12 text-center">
          <a
            href="https://wa.me/8618218291826"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <Button className="bg-green-500 hover:bg-green-600 text-white h-14 px-8 text-lg font-bold shadow-xl">
              <MessageCircle className="w-5 h-5 mr-2" />
              Chat on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      {/* Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="shadow-2xl">
            <div className="bg-red-600 text-white p-10">
              <h2 className="text-3xl md:text-4xl font-black">{t("b2b_hero")}</h2>
              <p className="text-xl mt-2">{t("b2b_sub")}</p>
            </div>
            <CardContent className="p-10">
              {isSuccess ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-24 h-24 text-green-600 mx-auto mb-6" />
                  <h3 className="text-3xl font-black mb-4">{t("b2b_submitted")}</h3>
                  <p className="text-xl text-gray-600">{t("b2b_submitted_desc")}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>{t("b2b_company")}</Label>
                      <Input required value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} />
                    </div>
                    <div>
                      <Label>{t("b2b_contact_name")}</Label>
                      <Input required value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Email *</Label>
                      <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <div>
                      <Label>{t("contact_phone")}</Label>
                      <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>{t("b2b_business_type")}</Label>
                      <Select value={formData.website} onValueChange={(v) => setFormData({...formData, website: v})}>
                        <SelectTrigger><SelectValue placeholder="Select business type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Distributor">Distributor</SelectItem>
                          <SelectItem value="Workshop">Workshop / Mechanic</SelectItem>
                          <SelectItem value="Reseller">Reseller</SelectItem>
                          <SelectItem value="Fleet">Fleet Operator</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t("b2b_volume")}</Label>
                      <Select value={formData.audience_size} onValueChange={(v) => setFormData({...formData, audience_size: v})}>
                        <SelectTrigger><SelectValue placeholder="Monthly units" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0-1000">1 – 50</SelectItem>
                          <SelectItem value="1000-5000">51 – 200</SelectItem>
                          <SelectItem value="5000-10000">201 – 500</SelectItem>
                          <SelectItem value="10000-50000">500+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>{t("b2b_notes")}</Label>
                    <Textarea rows={5} value={formData.why_join} onChange={(e) => setFormData({...formData, why_join: e.target.value})} />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-red-600 hover:bg-red-700 h-16 text-xl">
                    {isSubmitting ? t("b2b_applying") : t("b2b_apply")}
                  </Button>
                  <p className="text-center text-gray-500 text-sm">
                    Questions? WhatsApp/Call the {warehouse.id.toUpperCase()} warehouse: {warehouse.phone}
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}